// El punto de entrada del widget: lo que carga la landing.
//
//   <div data-rv-buscador data-locale="es" data-marca="655057"></div>
//   <script src="https://renfeviajes.renfe.com/widget/embed.<hash>.js" async></script>
//
// Y con las estaciones ya puestas, para una landing de campaña:
//
//   <div data-rv-buscador data-estacion-origen="MADRID (TODAS)"
//        data-estacion-destino-fija="BARCELONA (TODAS)"></div>
//
// O con sólo algunas pestañas, o sin barra de pestañas:
//
//   <div data-rv-buscador data-pestanas="trenhotel,hoteles,coches,ocio"></div>
//   <div data-rv-buscador data-sin-pestanas data-tab="actividades"></div>
//
// Y con los colores de la landing:
//
//   <div data-rv-buscador data-color-marca="#0a5" data-color-boton="#063"></div>
//
// Y un buscador de un solo producto, sin pestañas (el que abren los botones de las landings):
//
//   <div data-rv-buscador data-producto="tren-coche"></div>
//
// que también se puede abrir en un modal, con una instancia nueva que no toca la de la página:
//
//   RenfeViajesBuscador.abrirEnModal({ producto: 'tren-hotel', marca: '581296' })
//
// Toda la configuración va en `data-*` del propio hueco y NUNCA en un objeto JS global. Dos
// razones: un `<script>` inline de configuración exige `unsafe-inline` o un nonce en la CSP de
// la landing, y con `<script async>` hay carrera de orden entre este fichero y ese inline.
//
// El script se puede cargar tantas veces como quiera la landing y montar tantos buscadores
// como huecos haya: cada instancia lleva su propio estado y sus propios id.
import './estilos.css'
import './estilos-agente.css'
import { Buscador } from './buscador.js'
import { abrirModalBuscador } from './modal-buscador.js'
import { COLORES, MEDIDAS } from './montar.js'
import { textos as textosEs } from './textos.es.js'
import { textos as textosEn } from './textos.en.js'
import { textos as textosFr } from './textos.fr.js'

// Los tres idiomas van CONGELADOS aquí dentro (decisión 8). A cambio de no poder cambiar un
// rótulo con un UPDATE, el widget no necesita una llamada más para pintarse.
const TEXTOS = { es: textosEs, en: textosEn, fr: textosFr }
const IDIOMAS = Object.keys(TEXTOS)

const HUECOS = '[data-rv-buscador]'
const MONTADO = 'rvbMontado'

/**
 * De dónde cuelgan las llamadas y las URLs de compra: el dominio desde el que se sirve este
 * script. Así una landing no tiene que configurarlo, y si el widget se sirve desde otro sitio
 * (una preproducción, por ejemplo) todo lo sigue.
 */
function origenDelServicio () {
  try {
    const script = document.currentScript || [...document.querySelectorAll('script[src]')]
      .filter((s) => /\/widget\/embed[.-]/.test(s.src)).pop()
    if (script && script.src) return new URL(script.src).origin
  } catch (e) { /* abajo */ }
  return null
}

const ORIGEN_SERVICIO = origenDelServicio()

/**
 * Lee la configuración de un hueco.
 */
function configuracionDe (hueco) {
  const d = hueco.dataset
  const locale = IDIOMAS.includes(d.locale) ? d.locale : 'es'

  return {
    locale,
    textos: TEXTOS[locale],
    marca: d.marca || '',
    pestana: d.tab || undefined,
    // `data-portada` sólo lo pone la home de renfeviajes. Allí el buscador NO es un bloque
    // más: flota sobre el carrusel de portada y no ocupa sitio, que es su diseño de siempre.
    // En cualquier otra página eso dejaría el hueco con altura cero, así que por defecto el
    // widget fluye con el contenido.
    portada: undefined !== d.portada,
    // `data-origen-servicio` es la vía de escape para una preproducción; en condiciones
    // normales sale del src de este propio script.
    origen: d.origenServicio || ORIGEN_SERVICIO || undefined,

    // Las estaciones que la landing quiere ya puestas. Valen el código (MADRI), el id o el
    // nombre; el widget las resuelve contra el servidor porque lo que necesita para buscar no
    // es el texto, es la fila entera.
    //
    // Se llaman `estacion-origen` y no `origen` a secas para que nadie las confunda con
    // `origen-servicio`, que es otra cosa: el dominio del que cuelga el widget.
    //
    // La variante `-fija` hace lo mismo y además deja el campo en sólo lectura, para una
    // landing de un destino concreto donde no se quiere que el cliente se vaya a otro sitio.
    // Qué pestañas se ven, separadas por comas. Se admiten los nombres de dentro
    // (`paquetetren`, `trenhotelactividad`, `hotel`, `coche_alquiler`, `actividades`) y los
    // que usaría cualquiera (`tren`, `trenhotel`, `hoteles`, `coches`, `ocio`). Sin esto se
    // ven todas, que es lo de siempre.
    pestanas: d.pestanas || undefined,

    // Los colores, para que el widget se vea como la landing y no como renfeviajes:
    // `data-color-marca`, `data-color-boton`, `data-color-texto`, `data-color-texto-suave`,
    // `data-color-borde`, `data-color-separador`, `data-color-foco` y `data-color-error`.
    colores: coloresDe(d),

    // La maqueta: `apilada` pone el rótulo encima del campo, los campos dentro de una sola
    // píldora y las fechas y el botón en dos columnas. Sin esto, la de renfeviajes.
    maqueta: d.maqueta || undefined,

    // Con qué arranca el viaje: `solo-ida` o `ida-vuelta`. Sin esto manda el markup, que no
    // dice lo mismo en todos los bloques.
    tipoViaje: d.tipoViaje || undefined,

    // A qué proceso de compra de destino entra la rama de Viaje+: `nuevo` manda a
    // `CompraDestinoNew`, el rediseño. Sin decir nada, a `CompraDestino`, que es lo que hace
    // hoy la portada de renfeviajes.
    compraDestino: d.compraDestino || undefined,

    // El buscador entero a la vista desde el principio, en vez de arrancar en corto.
    desplegado: undefined !== d.desplegado,

    // Y las medidas de esa maqueta: esquinas, alto de campo, borde, relleno, letra y huecos.
    medidas: medidasDe(d),

    // Y la barra de pestañas entera, que se puede quitar: para una landing de una sola cosa,
    // donde el selector no pinta nada. Vale el atributo suelto (`data-sin-pestanas`) y también
    // decirlo en `data-pestanas`, que es lo que sale escribir.
    sinPestanas: undefined !== d.sinPestanas
      || ['no', 'ninguna', 'none', '0'].includes(String(d.pestanas || '').trim().toLowerCase()),

    // El catálogo de estaciones, al montar y en segundo plano: una petición cacheada que deja
    // el autocompletado instantáneo. Va encendido salvo que la landing diga que no.
    precargarEstaciones: 'no' !== d.precargarEstaciones && 'false' !== d.precargarEstaciones,

    // Un buscador de un solo producto: `tren-hotel` (sin la pregunta del hotel) o `tren-coche`
    // (el de la página de coches). Esconde las pestañas. Ver PRODUCTOS en buscador.js.
    producto: d.producto || undefined,

    // El título de la caja cuando el buscador se abre en un modal. Sin él, el del producto.
    titulo: d.titulo || undefined,

    estacionOrigen: d.estacionOrigenFija || d.estacionOrigen || undefined,
    estacionDestino: d.estacionDestinoFija || d.estacionDestino || undefined,
    origenFijo: undefined !== d.estacionOrigenFija,
    destinoFijo: undefined !== d.estacionDestinoFija,
  }
}

/**
 * Los `data-color-*` del hueco, con el nombre del papel que cumple cada uno.
 * `data-color-texto-suave` llega al dataset como `colorTextoSuave`.
 */
function coloresDe (d) {
  const colores = {}
  for (const papel of Object.keys(COLORES)) {
    const clave = 'color' + enCamello(papel).charAt(0).toUpperCase() + enCamello(papel).slice(1)
    if (d[clave]) colores[papel] = d[clave]
  }
  return Object.keys(colores).length ? colores : undefined
}

/**
 * Los `data-*` de medidas del hueco. `data-alto-campo` llega al dataset como `altoCampo`.
 */
function medidasDe (d) {
  const medidas = {}
  for (const nombre of Object.keys(MEDIDAS)) {
    const clave = enCamello(nombre)
    if (d[clave]) medidas[nombre] = d[clave]
  }
  return Object.keys(medidas).length ? medidas : undefined
}

/** `radio-campo` -> `radioCampo`, que es como lo deja el navegador en el dataset. */
function enCamello (nombre) {
  return nombre.split('-').map((t, i) => (i ? t.charAt(0).toUpperCase() + t.slice(1) : t)).join('')
}

/**
 * Monta el buscador en un hueco, una sola vez.
 * @returns {Buscador|null}
 */
export function montarEn (hueco) {
  if (!hueco || hueco.dataset[MONTADO] === '1') return null
  hueco.dataset[MONTADO] = '1'

  try {
    return new Buscador(Object.assign({ destino: hueco }, configuracionDe(hueco)))
  } catch (e) {
    // Si el widget falla, la landing de un tercero tiene que seguir en pie. Se avisa por
    // consola y se deja el hueco vacío, no a medio pintar.
    console.error('[buscador renfeviajes] no he podido montar el widget:', e)
    hueco.dataset[MONTADO] = 'error'
    hueco.innerHTML = ''
    return null
  }
}

/**
 * Monta todos los huecos que haya en la página.
 * @returns {Array<Buscador>}
 */
export function montarTodos (raiz = document) {
  return [...raiz.querySelectorAll(HUECOS)].map(montarEn).filter(Boolean)
}

// El script se carga con `async`, así que puede llegar antes o después de que el DOM esté
// listo. Las dos cosas tienen que funcionar.
function arrancar () {
  montarTodos()
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', arrancar, { once: true })
} else {
  arrancar()
}

// La API para lo que no cabe en un atributo: montar a mano un hueco que aparece después
// (una landing con pestañas, un modal que se abre tarde).
const api = {
  montar: (donde, opciones = {}) => {
    const hueco = typeof donde === 'string' ? document.querySelector(donde) : donde
    if (!hueco) return null
    // Lo que se pase por código manda sobre los data-*.
    if (hueco.dataset[MONTADO] === '1') return null
    hueco.dataset[MONTADO] = '1'
    return new Buscador(Object.assign({ destino: hueco }, configuracionDe(hueco), opciones))
  },
  montarTodos,

  /**
   * Abre el buscador en un modal, con una INSTANCIA NUEVA: el que ya haya en la página se queda
   * como está. Al cerrar el modal la instancia se destruye.
   *
   * Recibe o un hueco sin montar con sus `data-*` —que es lo cómodo si la landing ya sabe
   * construir el del buscador de la página: el mismo con `data-producto`— o las opciones en un
   * objeto. Lo que vaya en el segundo argumento manda sobre los `data-*`, como en montar().
   *
   * @param {Element|object} [fuente]
   * @param {object} [opciones]
   * @returns {{buscador: Buscador|null, cerrar: Function}}
   */
  abrirEnModal: (fuente = {}, opciones = {}) => {
    const esHueco = typeof Element !== 'undefined' && fuente instanceof Element
    const hueco = esHueco ? fuente : document.createElement('div')
    const extra = esHueco ? opciones : Object.assign({}, fuente, opciones)
    // Marcado como montado: que un montarTodos() posterior no le monte otro encima.
    hueco.dataset[MONTADO] = '1'
    // El buscador entero a la vista: en un modal no tiene sentido arrancar en corto.
    const configuracion = Object.assign({}, configuracionDe(hueco), { desplegado: true }, extra)
    return abrirModalBuscador({
      hueco,
      configuracion,
      crear: () => new Buscador(Object.assign({ destino: hueco }, configuracion)),
    })
  },

  idiomas: IDIOMAS,
}

if (typeof window !== 'undefined') window.RenfeViajesBuscador = api

export default api
